<template>
  <!-- Add an wrapper div here to avoid Vue throw error when isProductRestrictedInLocation is changed 
    The reason is that there is a v-show on router-view
  -->

  <div
    class="container page product"
    :class="{
      'product-ui-landing': productStore.isUILandingProduct,
      'product-ui-new-version': isProductUINewVersion,
      [productTypeClass]: productTypeClass,
      [productHandleClass]: productHandleClass,
    }"
  >
    <TakeDownContent v-if="isProductRestrictedInLocation"></TakeDownContent>
    <template v-else>
      <div class="product__breadcrumb">
        <sib-breadcrumb :items="breadcrumbItems"></sib-breadcrumb>
      </div>
      <div class="product-not-found" v-if="productStore.isProductNotFound">
        Product not found!
      </div>
      <section v-else class="product-page-container">
        <header class="product__header">
          <h1 class="product__title">
            <template v-if="!product">
              <sib-skeleton-loading round="sm"></sib-skeleton-loading>
              <sib-skeleton-loading
                round="sm"
                width="75%"
              ></sib-skeleton-loading>
            </template>
            <template v-else>
              {{ productTitleBaseOnABTest ? productTitleBaseOnABTest : product.title }}
            </template>
          </h1>

          <h2 class="product__price">
            <span class="product__price-final" v-if="finalDisCountApplied">
              {{ formatPrice(finalDisCountApplied?.price) }}
            </span>
            <sib-skeleton-loading class="mb-sm" v-else round="sm" width="6em">
            </sib-skeleton-loading>
            <span
              class="product__price-compare"
              v-if="finalDisCountApplied?.compareAtPrice"
            >
              {{ formatPrice(finalDisCountApplied?.compareAtPrice) }}
            </span>
          </h2>
          <div
            class="product__google-discount-messages"
            v-if="googleDiscountCode && !isCampaignDiscount"
          >
            <p>
              {{ googleDiscountMessage }}

              <span
                v-if="!googleDiscountApplied"
                class="product__google-discount-action"
                @click="applyGoogleDiscount"
              >
                CLAIM NOW</span
              >
            </p>
          </div>
          <div
            class="product__discount-messages mt-xs"
            v-if="
              (finalDisCountApplied?.messages?.length ||
                packDiscountApplied?.messages?.length) &&
              !priceDonated &&
              !googleDiscountCode
            "
          >
            <span
              class="product__discount-message"
              v-for="(message, index) of packDiscountApplied?.messages ||
              finalDisCountApplied?.messages"
              :key="index"
            >
              {{ message }}
            </span>
          </div>

          <div
            class="product__discount-messages mt-xs"
            v-if="descriptionDonation && priceDonated && !googleDiscountCode"
            v-html="
              descriptionDonation.replaceAll(
                `{price}`,
                formatPrice(priceDonated)
              )
            "
          ></div>

          <!-- Displays the discount percentage for products configured to compare price in the store -->
          <div
            class="product__discount-messages mt-xs"
            v-if="
              !finalDisCountApplied?.messages?.length &&
              !packDiscountApplied?.messages?.length &&
              !isCampaignDiscount &&
              !discountStore.discountCode &&
              !priceDonated &&
              !googleDiscountCode &&
              percentDiscountDefault
            "
          >
            <span
              class="product__discount-message product__discount-message-secondary"
            >
              {{ percentDiscountDefault }}% off
            </span>
          </div>

          <div class="product__sumary-review mt-sm" style="height: 20px">
            <div
              v-if="productStore.product?.id"
              v-show="jdgmPreviewBadgeLoading"
              :key="productStore.product?.id"
              class="jdgm-widget jdgm-preview-badge"
              :data-id="productRawId"
            ></div>
            <div v-show="!jdgmPreviewBadgeLoading">
              <sib-skeleton-loading round="sm" width="8em" height="19px">
              </sib-skeleton-loading>
              <sib-skeleton-loading
                class="ml-xs"
                round="sm"
                width="7em"
                height="19px"
              >
              </sib-skeleton-loading>
            </div>
          </div>
        </header>
        <ProductImages />
        <div class="product__info">
          <template v-if="productStore.isUILandingProduct">
            <ProductForm />
            <client-only
              v-if="
                !productUniqueGiftDynamicDescriptionAndShipping &&
                productUniqueGiftFixedDescriptionAndShippingCustom
              "
            >
              <div
                v-html="productUniqueGiftFixedDescriptionAndShippingCustom"
              ></div>
            </client-only>
            <template v-else>
              <client-only
                :is-show="componentStore.productPage.isShowProductDescription"
              >
                <template #placeholder>
                  <div
                    :data-key="PRODUCT_DESCRIPTION_COMPONENT_KEY"
                    ref="productDescriptionPlaceholder"
                  ></div>
                  <sib-skeleton-loading
                    height="5em"
                    class="mt-sm"
                  ></sib-skeleton-loading>
                </template>
                <ProductDescription />
              </client-only>
              <client-only
                :is-show="componentStore.productPage.isShowProductShipping"
              >
                <template #placeholder>
                  <div
                    :data-key="SHIPPING_COMPONENT_KEY"
                    ref="shippingPlaceholder"
                  ></div>
                  <sib-skeleton-loading
                    height="5em"
                    class="mt-sm"
                  ></sib-skeleton-loading>
                </template>
                <ProductShipping />
              </client-only>
              <client-only
                v-if="!isProductUINewVersion"
                :is-show="componentStore.productPage.isShowSocialSharing"
              >
                <template #placeholder>
                  <div
                    :data-key="SOCIAL_SHARING_COMPONENT_KEY"
                    ref="socialSharingPlaceholder"
                  ></div>
                  <sib-skeleton-loading
                    height="3em"
                    class="mt-sm"
                  ></sib-skeleton-loading>
                </template>
                <div class="product__option" v-if="product">
                  <h2 class="bordered-bottom">Share</h2>
                  <SocialSharing
                    :title="product.title"
                    :image="product.featuredImage?.src"
                    :socials="['facebook', 'whatsapp', 'twitter', 'pinterest']"
                  />
                </div>
              </client-only>
            </template>
          </template>
          <template v-else>
            <template v-if="isQrGreetingCardProductType">
              <h4 class="mt-md mb-sm">
                Video Demo of QR Greeting Card with Digital Content
              </h4>
              <sib-video
                :style="'width:100%'"
                src="https://cdn.shopify.com/videos/c/o/v/aebb1d1b559141738fa836ee372c0cf6.mp4"
                autoplay
              ></sib-video>
            </template>

            <template v-if="!isDigitalProductProductType">
              <ProductForm />
              <ProductNote />
              <BuyMoreSaveMore
                v-if="shouldShowBuyMoreSaveMore && !isProductUINewVersion"
              />
            </template>
            <template v-if="isDigitalProductProductType">
              <client-only
                ><sib-button
                  role="primary"
                  @click="downloadArtwork()"
                  :loading="loading"
                >
                  DOWNLOAD ARTWORK
                </sib-button>
                <p class="error-message" v-show="isError">
                  {{ errorMessage }}
                </p>
              </client-only>
            </template>
            <!-- Using client only for under the fold components to reduce the number of DOM that server needs to render
            Thought, increasing the server response time and reducing the page size
        -->
            <client-only
              :is-show="componentStore.productPage.isShowPeopleViewing"
            >
              <template #placeholder>
                <div
                  ref="peopleViewingPlaceholder"
                  :data-key="PEOPLE_VIEWING_COMPONENT_KEY"
                ></div>
                <sib-skeleton-loading
                  height="2em"
                  class="mt-sm"
                ></sib-skeleton-loading>
              </template>
              <PeoplesViewing v-if="product" />
            </client-only>

            <client-only
              :is-show="componentStore.productPage.isShowProductDescription"
            >
              <template #placeholder>
                <div
                  :data-key="PRODUCT_DESCRIPTION_COMPONENT_KEY"
                  ref="productDescriptionPlaceholder"
                ></div>
                <sib-skeleton-loading
                  height="5em"
                  class="mt-sm"
                ></sib-skeleton-loading>
              </template>
              <ProductDescription />
            </client-only>

            <client-only
              v-if="!isDigitalContentProductType"
              :is-show="componentStore.productPage.isShowProductShipping"
            >
              <template #placeholder>
                <div
                  :data-key="SHIPPING_COMPONENT_KEY"
                  ref="shippingPlaceholder"
                ></div>
                <sib-skeleton-loading
                  height="5em"
                  class="mt-sm"
                ></sib-skeleton-loading>
              </template>
              <ProductShipping />
            </client-only>
            <client-only
              v-if="!isProductUINewVersion"
              :is-show="componentStore.productPage.isShowSocialSharing"
            >
              <template #placeholder>
                <div
                  :data-key="SOCIAL_SHARING_COMPONENT_KEY"
                  ref="socialSharingPlaceholder"
                ></div>
                <sib-skeleton-loading
                  height="3em"
                  class="mt-sm"
                ></sib-skeleton-loading>
              </template>
              <div class="product__option" v-if="product">
                <h2 class="bordered-bottom">Share</h2>
                <SocialSharing
                  :title="product.title"
                  :image="product.featuredImage?.src"
                  :socials="['facebook', 'whatsapp', 'twitter', 'pinterest']"
                />
              </div>
            </client-only>
          </template>
        </div>
      </section>

      <template v-if="productStore.isUILandingProduct">
        <template
          v-for="(section, index) in productUniqueGiftLayoutComponentData"
          :key="index"
        >
          <client-only
            :is-show="
              shouldShowUniqueGiftProductComponents[section._key] || false
            "
          >
            <template #placeholder>
              <div
                ref="uniqueGiftComponentplaceholders"
                :data-key="section._key"
              ></div>
              <sib-skeleton-loading
                height="10rem"
                class="mb-sm"
              ></sib-skeleton-loading>
            </template>
            <component
              :is="productComponents[section._type]"
              :section="section"
              :productSeeMoreCollections="productSeeMoreCollections"
            />
          </client-only>
        </template>
      </template>
      <template v-else-if="isProductUINewVersion">
        <template
          v-for="(section, index) in productUINewVersionLayoutComponentData"
          :key="index"
        >
          <client-only
            :is-show="shouldShowUINewVersionComponents[section._key] || false"
          >
            <template #placeholder>
              <div
                ref="uiNewVersionplaceholders"
                :data-key="section._key"
              ></div>
              <sib-skeleton-loading
                height="10rem"
                class="mb-sm"
              ></sib-skeleton-loading>
            </template>
            <component
              :is="productComponents[section._type]"
              :section="section"
              :productSeeMoreCollections="productSeeMoreCollections"
              :discountAppliedFirstProductVariant="
                discountAppliedFirstProductVariant
              "
            />
          </client-only>
        </template>
      </template>
      <template v-else>
        <client-only
          :is-show="componentStore.productPage.isShowProductRecommendation"
        >
          <template #placeholder>
            <div
              :data-key="PRODUCT_RECOMMENDATION_COMPONENT_KEY"
              ref="productRecommendationPlaceholder"
            ></div>
            <sib-skeleton-loading
              height="7em"
              class="mt-lg"
            ></sib-skeleton-loading>
          </template>
          <Divider />
          <ProductRecommendation
            :productSeeMoreCollections="productSeeMoreCollections"
          />
        </client-only>

        <!-- <client-only
        :is-show="componentStore.productPage.isShowProductRecentlyViewedItems"
      >
        <template #placeholder>
          <div
            :data-key="RECENTLY_VIEWED_COMPONENT_KEY"
            ref="recentlyViewedProductsPlaceholder"
          ></div>
          <sib-skeleton-loading
            height="7em"
            class="mt-lg"
          ></sib-skeleton-loading>
        </template>
        <Divider />
        <ProductRecentlyViewedItems />
      </client-only> -->

        <client-only :is-show="componentStore.productPage.isShowProductVideo">
          <template #placeholder>
            <div
              :data-key="PRODUCT_VIDEO_COMPONENT_KEY"
              ref="productVideoPlaceholder"
            ></div>
            <sib-skeleton-loading
              height="7em"
              class="mt-lg"
            ></sib-skeleton-loading>
          </template>
          <ProductVideo />
        </client-only>

        <client-only>
          <Divider />
          <component :is="productComponents['judgemeReviews']" />
        </client-only>
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import ProductImages from './ProductImages.vue'
import ProductForm from './ProductForm.vue'
import ProductNote from './ProductNote.vue'
import BuyMoreSaveMore from './BuyMoreSaveMore.vue'
import Divider from '@/components/Divider.vue'
import ProductVideo from './ProductVideo.vue'
import {
  buildCollectionUrl,
  formatPrice,
  getRawIdFromShopifyId,
  isProductHasTags,
  isProductHasType,
  isProductHasTypeStartWith,
  extractProductCodeAndNicheCodeFromHandle,
} from '@/utils/product'
import { useProduct } from './composables/Product'
import {
  onMounted,
  watch,
  nextTick,
  ref,
  defineAsyncComponent,
  Ref,
  WatchStopHandle,
  onUnmounted,
  reactive,
} from 'vue'
import { useProductRecentlyViewedItems } from './composables/ProductRecentlyViewed'
import { useApplyFakePriceDiscountBF9 } from './composables/ProductFakePrice'
import { computed } from '@vue/reactivity'
import { useRoute } from 'vue-router'
import useComponentStore from '@/store/component'
import { runTaskOnIdleTime } from '@/utils/idlize'
import {
  EVENT_SCROLL_TO_REVIEW_SECTION,
  PRODUCT_AVERAGE_REVIEW_META_FIELD_KEY,
  PRODUCT_REVIEW_META_FIELD_NAMESPACE,
  HEADER_HEIGHT,
  PRODUCT_TYPE_GREETING_CARD,
  PACK_OPTIONS,
  CAMPAIGN_DISCOUNT_BLACKFRIDAY9,
  PERCENTAGE_DISCOUNT_BF9_FOR_PACK_OPTION_PERCENTAGE,
  PRODUCT_TYPE_DIGITAL_PRODUCT,
  EVENT_OPEN_POPUP_SIGN_IN,
  PRODUCT_TYPE_QR_GREETING_CARD,
  PRODUCT_TAG_PROMOTION_NO,
  PRODUCT_TAG_SHIP_FROM_US,
  SEVEN_DAYS_IN_MILLISECONDS,
  CACHE_KEY_DISCOUNT_CODE,
  PROMOTION_GOOGLE_DISCOUNT_MESSAGE,
  PROMOTION_GOOGLE_DISCOUNT_MESSAGE_APPLIED,
  AB_TEST_PRODUCT_TITLE,
  AB_TEST_VARIANT_PRODUCT_CASE_A,
  AB_TEST_VARIANT_PRODUCT_CASE_B,
  AB_TEST_VARIANT_PRODUCT_CASE_C,
  AB_TEST_PRODUCT_PAGE_UI_NEW_VERSION,
  AB_TEST_VARIANT_DISABLE,
  AB_TEST_VARIANT_ENABLE,
  EVENT_ABTEST_ASSIGNED,
  MIXPANEL_DESTINATION,
  destinationIntegrations,
  VN_COUNTRY_CODE,
  CHINA_COUNTRY_CODE,
  EVENT_NEW_PRODUCT_PAGE_SCROLL,
  debounce,
  PRODUCT_TAG_IGN_PACK_DISCOUNT,
  AB_TEST_PRODUCT_COMBO,
  PRODUCT_TAG_APPLY_FAKE_PRICE_BF9_BUT_NO_DISCOUNT,
  CAMPAIGN_FAKE_PRICE_BF9_BUT_NO_DISCOUNT,
  AB_TEST_NO_APPLY_PACK_DISCOUNT,
  PRODUCT_HANDLES_NO_APPLY_PACK_DISCOUNT,
  QUERY_STRING_UTM_CAMPAIGN,
  EVENT_SHOW_AVERAGE_START_PRE_BAGDE,
  PROMOTION_BAR_TYPE_DISCOUNT,
  PROMOTION_BAR_PRIORITY_MEDIUM,
} from '@/utils'
import EventBus from '@/services/eventbus'
import useSettingStore from '@/store/setting'
import useProductStore from '@/store/product'
import Logger from '@/services/log'
import useDeviceStore from '@/store/device'
import Analytics from '@/services/analytics'
import useDiscountStore from '@/store/discount'
import useCartStore from '@/store/cart'
import useMediaCampaignStore from '@/store/media'
import useCustomerStore from '@/store/customer'
import Provider from '@/provider'
import CacheService from '@/services/cache'
import useDiscount from '@/composables/applyDiscount'
import { buildGoogleDiscountMessage } from '@/utils/discount'
import { useABTest } from '@/composables/useABTest'
import useRequestLocationStore from '@/store/requestLocation'
import { useProductCombo } from './composables/useProductCombo'

const { applyDiscount, getValueFromDiscountCode } = useDiscount()
const customerStore = useCustomerStore()
const { getABTestActivedVariantByCode } = useABTest()
const requestLocation = useRequestLocationStore()
const { getComboCartLineItemNotCompleted } = useProductCombo()
const { applyFakePriceDiscountBF9 } = useApplyFakePriceDiscountBF9()

const comboCartLineItemNotCompleted = ref(null)

const customer = computed(() => customerStore.customer)

const ProductRecommendation = defineAsyncComponent(
  () => import('./ProductRecommendation.vue')
)
const TakeDownContent = defineAsyncComponent(
  () => import('@/components/TakeDownContent.vue')
)
const ProductDescription = defineAsyncComponent(
  () => import('./ProductDescription.vue')
)
const ProductShipping = defineAsyncComponent(
  () => import('./ProductShipping.vue')
)
const PeoplesViewing = defineAsyncComponent(
  () => import('./PeoplesViewing.vue')
)
const SocialSharing = defineAsyncComponent(
  () => import('@/components/SocialSharing.vue')
)

const productComponents: { [key: string]: any } = {
  baseSection: defineAsyncComponent(
    () => import('@/components/product/ProductBaseSection.vue')
  ),
  customHtml: defineAsyncComponent(
    () => import('@/components/product/ProductCustomHtml.vue')
  ),
  videoReviews: defineAsyncComponent(
    () => import('@/components/product/ProductVideoReviews.vue')
  ),
  imageReviews: defineAsyncComponent(
    () => import('@/components/product/ProductImageReviews.vue')
  ),
  shopNowButton: defineAsyncComponent(
    () => import('@/components/product/ShopNowButton.vue')
  ),
  metaFieldProduct: defineAsyncComponent(
    () => import('@/components/product/ProductMetaField.vue')
  ),
  judgemeReviews: defineAsyncComponent(
    () => import('./ProductReviewJudgeme.vue')
  ),
  recommendation: defineAsyncComponent(
    () => import('./ProductRecommendation.vue')
  ),
  recommendMoreItems: defineAsyncComponent(
    () => import('@/components/product/ProductRecommendMoreItems.vue')
  ),
  recommendNonNicheItems: defineAsyncComponent(
    () => import('@/components/product/ProductRecommendNonNicheItems.vue')
  ),
  recommendSimilarItems: defineAsyncComponent(
    () => import('@/components/product/ProductRecommendSimilarItems.vue')
  ),
  blogs: defineAsyncComponent(
    () => import('@/components/product/ProductBlogs.vue')
  ),
  comparePriceOtherSite: defineAsyncComponent(
    () => import('@/components/product/ProductComparePriceOtherSite.vue')
  ),
}

const shouldShowUniqueGiftProductComponents = reactive<{
  [key: string]: boolean
}>({})
const shouldShowUINewVersionComponents = reactive<{ [key: string]: boolean }>(
  {}
)
const uniqueGiftComponentplaceholders = ref([])
const uiNewVersionplaceholders = ref([])

const PEOPLE_VIEWING_COMPONENT_KEY = 'people-viewing'
const PRODUCT_DESCRIPTION_COMPONENT_KEY = 'product-description'
const SHIPPING_COMPONENT_KEY = 'shipping'
const SOCIAL_SHARING_COMPONENT_KEY = 'social-sharing'
const PRODUCT_VIDEO_COMPONENT_KEY = 'product-video'
const PRODUCT_REVIEW_COMPONENT_KEY = 'product-review'
const PRODUCT_RECOMMENDATION_COMPONENT_KEY = 'product-recommendation'
const RECENTLY_VIEWED_COMPONENT_KEY = 'recently-viewed'

const peopleViewingPlaceholder = ref<HTMLDivElement | null>(null)
const productDescriptionPlaceholder = ref<HTMLDivElement | null>(null)
const shippingPlaceholder = ref<HTMLDivElement | null>(null)
const socialSharingPlaceholder = ref<HTMLDivElement | null>(null)
const productVideoPlaceholder = ref<HTMLDivElement | null>(null)
const productReviewPlaceholder = ref<HTMLDivElement | null>(null)
const productRecommendationPlaceholder = ref<HTMLDivElement | null>(null)
const recentlyViewedProductsPlaceholder = ref<HTMLDivElement | null>(null)
let observer: IntersectionObserver | null = null

const placeHolderAndHandlerMap: {
  [key: string]: {
    placeholder: Ref<HTMLDivElement | null>
    handler: () => void
  }
} = {
  [PEOPLE_VIEWING_COMPONENT_KEY]: {
    placeholder: peopleViewingPlaceholder,
    handler() {
      componentStore.productPage.isShowPeopleViewing = true
      Logger.log('showing people viewing component', 3)
    },
  },
  [PRODUCT_DESCRIPTION_COMPONENT_KEY]: {
    placeholder: productDescriptionPlaceholder,
    handler() {
      componentStore.productPage.isShowProductDescription = true
      Logger.log('showing product description component', 3)
    },
  },
  [SHIPPING_COMPONENT_KEY]: {
    placeholder: shippingPlaceholder,
    handler() {
      componentStore.productPage.isShowProductShipping = true
      Logger.log('showing product shipping component', 3)
    },
  },
  [SOCIAL_SHARING_COMPONENT_KEY]: {
    placeholder: socialSharingPlaceholder,
    handler() {
      componentStore.productPage.isShowSocialSharing = true
      Logger.log('showing social sharing component', 3)
    },
  },
  [PRODUCT_VIDEO_COMPONENT_KEY]: {
    placeholder: productVideoPlaceholder,
    handler() {
      componentStore.productPage.isShowProductVideo = true
      Logger.log('showing product video component', 3)
    },
  },
  [PRODUCT_REVIEW_COMPONENT_KEY]: {
    placeholder: productReviewPlaceholder,
    handler() {
      componentStore.productPage.isShowProductReview = true
      Logger.log('showing product review component', 3)
    },
  },
  [PRODUCT_RECOMMENDATION_COMPONENT_KEY]: {
    placeholder: productRecommendationPlaceholder,
    handler() {
      componentStore.productPage.isShowProductRecommendation = true
      Logger.log('showing product recommendation component', 3)
    },
  },
  [RECENTLY_VIEWED_COMPONENT_KEY]: {
    placeholder: recentlyViewedProductsPlaceholder,
    handler() {
      componentStore.productPage.isShowProductRecentlyViewedItems = true
      Logger.log('showing recently viewed component', 3)
    },
  },
}

const mediaCampaignStore = useMediaCampaignStore()
const cartStore = useCartStore()
const discountStore = useDiscountStore()
const deviceStore = useDeviceStore()
const componentStore = useComponentStore()
const settingStore = useSettingStore()
const productStore = useProductStore()
const route = useRoute()
const {
  product,
  discountApplied,
  discountAppliedFirstProductVariant,
  isProductRestrictedInLocation,
  productSeeMoreCollections,
  isExpressShipping,
  isExpressShippingChina,
  trackViewedNichesAndViewedProductType,
} = useProduct()
const { saveProductToCache } = useProductRecentlyViewedItems()
const productAverageReview = ref<HTMLDivElement | null>(null)
const productAverageReviewHtml = computed(() => {
  return (
    product.value?.metafields?.find((elm) => {
      if (!elm) return false
      return (
        elm?.key == PRODUCT_AVERAGE_REVIEW_META_FIELD_KEY &&
        elm?.namespace == PRODUCT_REVIEW_META_FIELD_NAMESPACE
      )
    })?.value || ''
  )
})

const loading = ref(false)
const jdgmPreviewBadgeLoading = ref(false)
const isError = ref(false)
let errorMessage = ref('Something went wrong. Please try again later.')

const productTitleBaseOnABTest = computed(() => {
  if(!product.value) return ''
  const abtestVariant = getABTestActivedVariantByCode(
    AB_TEST_PRODUCT_TITLE
  )
  if (!abtestVariant) return ''
  if (product.value?.handle?.includes('alphabet-initial-for-whiskey-bourbon-lovers-personalized-whisky-whiskey-glass-10470280725scl1cle1ph08th07li09wkg1_ipatuwsk00064')) {
    if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_A) {
      return 'Signature Initial Customizable Glass for Whiskey, Bourbon Lovers'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_B) {
      return 'Golden Letter-Mark Whiskey Decanter Set - Fully Personalized With Heritage Theme'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_C) {
      return 'Gentleman’s Whiskey Ritual Set - Full Item Included Starter Collection'
    }
  } else if (product.value?.handle?.includes('whiskey-cigar-personalized-whisky-wallet-phone-case-10160040825scl1cle1ph08ha09li09lpc1_ipatuwsk00066')) {
    if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_A) {
      return 'Signature Whiskey Wallet Phone Case - Personalized for Him'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_B) {
      return 'Personalized Whiskey Connoisseur Set - Wallet Phone Case Included'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_C) {
      return 'Gentleman’s Whiskey Cigar Ritual Set - Full Item Included Phone Case & Charm'
    }
  } else if (product.value?.handle?.includes('whiskey-and-cigar-personalized-whisky-whiskey-glass-10346220325scl1cle1ph08ha09li09wkg1_ipatuwsk00008')) {
    if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_A) {
      return 'Customizable Glass for Whiskey, Cigar Lovers - Gift for Him'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_B) {
      return 'Signature Personalized Whisky Cigars Decanter Set - For Whiskey & Cigar Lovers'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_C) {
      return 'Gentleman’s Whiskey Connoisseur Set - Custom Engraved for Him'
    }
  } 
  return ''
})

const productUniqueGiftsLayoutData = computed(() => {
  if (!product.value) return
  const productLayoutComponentBaseOnHandle =
    settingStore.productLandingPage?.productLayoutComponents?.find(
      (item) => item.handle == product.value.handle
    )
  if (productLayoutComponentBaseOnHandle) {
    return productLayoutComponentBaseOnHandle
  }
  const productCodeAndNicheCodeFromHandle =
    extractProductCodeAndNicheCodeFromHandle(product.value.handle)

  const productLayoutComponentBaseOnProductTypeAndNiche =
    settingStore.productLandingPage?.productLayoutComponents?.find(
      (item) =>
        item.nicheCode &&
        item.productTypeCode &&
        productCodeAndNicheCodeFromHandle.includes(item.nicheCode) &&
        productCodeAndNicheCodeFromHandle.includes(item.productTypeCode)
    )

  if (productLayoutComponentBaseOnProductTypeAndNiche) {
    return productLayoutComponentBaseOnProductTypeAndNiche
  }

  const productLayoutComponentBaseOnNiche =
    settingStore.productLandingPage?.productLayoutComponents?.find(
      (item) =>
        item.nicheCode &&
        !item.productTypeCode &&
        productCodeAndNicheCodeFromHandle.includes(item.nicheCode)
    )

  if (productLayoutComponentBaseOnNiche) {
    return productLayoutComponentBaseOnNiche
  }

  const productLayoutComponentBaseOnProductType =
    settingStore.productLandingPage?.productLayoutComponents?.find(
      (item) =>
        item.productTypeCode &&
        !item.nicheCode &&
        productCodeAndNicheCodeFromHandle.includes(item.productTypeCode)
    )
  if (productLayoutComponentBaseOnProductType) {
    return productLayoutComponentBaseOnProductType
  }
  return
})

const productUniqueGiftLayoutComponentData = computed(() => {
  if (productUniqueGiftsLayoutData.value?.components?.length)
    return productUniqueGiftsLayoutData.value?.components
  return []
})

const productUniqueGiftFixedDescriptionAndShippingCustom = computed(() => {
  return productUniqueGiftsLayoutData.value?.descriptionAndShipping || ''
})
const productUniqueGiftDynamicDescriptionAndShipping = computed(() => {
  return (
    productUniqueGiftsLayoutData.value?.dynamicDescriptionAndShipping || false
  )
})

const productUINewVersionLayoutData = computed(() => {
  if (!product.value) return
  const productLayoutComponentBaseOnHandle =
    settingStore.productLandingPage?.productPageComponentsNewVersion?.find(
      (item) => item.handle == product.value.handle
    )
  if (productLayoutComponentBaseOnHandle) {
    return productLayoutComponentBaseOnHandle
  }
  const productCodeAndNicheCodeFromHandle =
    extractProductCodeAndNicheCodeFromHandle(product.value.handle)

  const productLayoutComponentBaseOnProductTypeAndNiche =
    settingStore.productLandingPage?.productPageComponentsNewVersion?.find(
      (item) =>
        item.nicheCode &&
        item.productTypeCode &&
        productCodeAndNicheCodeFromHandle.includes(item.nicheCode) &&
        productCodeAndNicheCodeFromHandle.includes(item.productTypeCode)
    )

  if (productLayoutComponentBaseOnProductTypeAndNiche) {
    return productLayoutComponentBaseOnProductTypeAndNiche
  }

  const productLayoutComponentBaseOnNiche =
    settingStore.productLandingPage?.productPageComponentsNewVersion?.find(
      (item) =>
        item.nicheCode &&
        !item.productTypeCode &&
        productCodeAndNicheCodeFromHandle.includes(item.nicheCode)
    )

  if (productLayoutComponentBaseOnNiche) {
    return productLayoutComponentBaseOnNiche
  }

  const productLayoutComponentBaseOnProductType =
    settingStore.productLandingPage?.productPageComponentsNewVersion?.find(
      (item) =>
        item.productTypeCode &&
        !item.nicheCode &&
        productCodeAndNicheCodeFromHandle.includes(item.productTypeCode)
    )
  if (productLayoutComponentBaseOnProductType) {
    return productLayoutComponentBaseOnProductType
  }
  if (
    settingStore.productLandingPage?.newUIProductPageComponentsDefault
      ?.components?.length
  ) {
    return settingStore.productLandingPage?.newUIProductPageComponentsDefault
  }
  return
})

const productUINewVersionLayoutComponentData = computed(() => {
  if (productUINewVersionLayoutData.value?.components?.length)
    return productUINewVersionLayoutData.value?.components
  return []
})

const isProductUINewVersion = computed(() => {
  const abtestVariant = getABTestActivedVariantByCode(
    AB_TEST_PRODUCT_PAGE_UI_NEW_VERSION
  )

  if (
    requestLocation.countryCode &&
    requestLocation.countryCode.toUpperCase() != VN_COUNTRY_CODE &&
    !CHINA_COUNTRY_CODE.includes(requestLocation.countryCode.toUpperCase()) && 
    settingStore.productLandingPage?.showNewVersionUIProductPage &&
    productUINewVersionLayoutComponentData.value?.length &&
    abtestVariant != AB_TEST_VARIANT_DISABLE
  ) {
    productStore.isProductUINewVersion = true
  } else {
    productStore.isProductUINewVersion = false
  }

  return productStore.isProductUINewVersion
})

const productRawId = computed(() =>
  getRawIdFromShopifyId(productStore.product?.id)
)

const isProductPackOption = computed(() => {
  if (!product.value) return []
  const index = product.value.options?.findIndex((item) =>
    item.name?.includes(PACK_OPTIONS)
  )
  return index > -1
})
const isDigitalProductProductType = computed(() => {
  return isProductHasType(productStore?.product, [PRODUCT_TYPE_DIGITAL_PRODUCT])
})
const isQrGreetingCardProductType = computed(() => {
  return isProductHasType(productStore?.product, [
    PRODUCT_TYPE_QR_GREETING_CARD,
  ])
})

const googleDiscountCode = computed(
  () => discountStore.googleDiscountCode || ''
)

const googleDiscountApplied = computed(
  () => discountStore.googleDiscountApplied
)

const googleDiscountMessage = computed(() => {
  if (!googleDiscountCode) return ''
  const price = product.value?.price || 0
  const percentageGoogleDiscount = getValueFromDiscountCode(
    googleDiscountCode.value
  )
  const discountAmount =
    +((price * percentageGoogleDiscount) / 100).toFixed(2) || 0
  const priceDiscount = +(price - discountAmount).toFixed(2) || 0

  if (googleDiscountApplied.value) {
    return buildGoogleDiscountMessage(
      googleDiscountCode.value,
      discountAmount,
      PROMOTION_GOOGLE_DISCOUNT_MESSAGE_APPLIED
    )
  } else {
    return buildGoogleDiscountMessage(
      googleDiscountCode.value,
      priceDiscount,
      PROMOTION_GOOGLE_DISCOUNT_MESSAGE
    )
  }
})

const isCampaignDiscountBlackfriday9 = computed(
  () =>
    discountStore.campaignDiscountCode?.toLowerCase() ==
    CAMPAIGN_DISCOUNT_BLACKFRIDAY9
)
const isCampaignDiscount = computed(() =>
  discountStore.campaignDiscountCode?.toLowerCase()
)

const noApplyPackDiscountProperties = computed(() => {
  if (!product.value) return false

  const utmCampaignQuery = route.query[QUERY_STRING_UTM_CAMPAIGN]
  let utmNoApplyPackDiscount = false

  const productHandle = product.value?.handle
  if (utmCampaignQuery && productHandle) {
    utmNoApplyPackDiscount =
      (productHandle.includes('fml5489') &&
        utmCampaignQuery.includes('fml5489_b')) ||
      (productHandle.includes('kid5186') &&
        utmCampaignQuery.includes('kid5186_b'))
  }

  const productHandlesNoApplyPackDiscount =
    PRODUCT_HANDLES_NO_APPLY_PACK_DISCOUNT.split(',')

  // if (
  //   productHandlesNoApplyPackDiscount.includes(productHandle) &&
  //   utmNoApplyPackDiscount
  // )
  //   return true

  const abtestVariant = getABTestActivedVariantByCode(
    AB_TEST_NO_APPLY_PACK_DISCOUNT
  )
  if (abtestVariant === null) return false
  if (abtestVariant === AB_TEST_VARIANT_ENABLE) {
    return true
  }
  return false
})

const packDiscountApplied = computed(() => {
  if (noApplyPackDiscountProperties.value) return
  if (
    isProductPackOption.value &&
    isCampaignDiscountBlackfriday9.value &&
    !isProductHasTags(productStore.product, [PRODUCT_TAG_IGN_PACK_DISCOUNT])
  ) {
    const price = productStore.selectedVariant?.price || 0
    return {
      amount: PERCENTAGE_DISCOUNT_BF9_FOR_PACK_OPTION_PERCENTAGE,
      amountFormated: `${PERCENTAGE_DISCOUNT_BF9_FOR_PACK_OPTION_PERCENTAGE}%`,
      compareAtPrice: price,
      price:
        price *
        ((100 - PERCENTAGE_DISCOUNT_BF9_FOR_PACK_OPTION_PERCENTAGE) / 100),
      messages: [`${PERCENTAGE_DISCOUNT_BF9_FOR_PACK_OPTION_PERCENTAGE}% off`],
    }
  }
  return
})

const isGreetingCardFlow = computed(() => mediaCampaignStore.greetingCardFlow)
const greetingCardDiscountApplied = computed(() => {
  const isProductGreetingCard = isProductHasType(product.value, [
    PRODUCT_TYPE_GREETING_CARD,
  ])
  const isProductTypeGreetingCardInLineItems =
    cartStore.localCartLineItems.find((lineItem) =>
      isProductHasType(lineItem?.merchandise?.product, [
        PRODUCT_TYPE_GREETING_CARD,
      ])
    )
  if (
    isGreetingCardFlow.value &&
    isProductGreetingCard &&
    cartStore.localCartLineItems.length &&
    !isProductTypeGreetingCardInLineItems
  ) {
    return {
      amount: 100,
      amountFormated: 100,
      compareAtPrice: product.value?.compareAtPrice || product.value?.price,
      price: 0,
    }
  }
  return
})

const donationDiscountApplied = computed(() => {
  if (priceDonated.value) {
    return {
      amount: 0,
      amountFormated: 0,
      compareAtPrice: product.value?.compareAtPrice || 0,
      price: product.value?.price || 0,
    }
  }
  return
})

const descriptionDonation = computed(() => {
  return mediaCampaignStore.kolCampaign?.description
})

const priceDonated = computed(() => {
  if (
    !mediaCampaignStore.donationCode ||
    !mediaCampaignStore.kolCampaign ||
    mediaCampaignStore.kolCampaign.donateConfiguration.length != 1
  )
    return ''
  const price =
    ((product.value?.price || 0) *
      mediaCampaignStore.kolCampaign.totalAmountPercentage) /
    100

  return price
})

const comboDiscountApplied = computed(() => {
  const abtestVariant = getABTestActivedVariantByCode(AB_TEST_PRODUCT_COMBO)
  if (
    !productStore.productComboSelected ||
    !comboCartLineItemNotCompleted.value ||
    abtestVariant == AB_TEST_VARIANT_DISABLE
  )
    return ''
  const price = product.value?.price || 0
  return {
    amount: productStore.productComboSelected.discountValue,
    compareAtPrice: price,
    price:
      price -
      (productStore.productComboSelected.discountType == 'amount'
        ? productStore.productComboSelected.discountValue
        : Math.ceil(
            price * (productStore.productComboSelected.discountValue / 100)
          )),
    messages: [
      `${
        productStore.productComboSelected.discountType == 'amount'
          ? '$' + productStore.productComboSelected.discountValue
          : productStore.productComboSelected.discountValue + '%'
      } off`,
    ],
  }
})

const hasFakePriceBF9ButNoDiscountProductTag = computed(() =>
  isProductHasTags(product.value, [
    PRODUCT_TAG_APPLY_FAKE_PRICE_BF9_BUT_NO_DISCOUNT,
  ])
)

const fakePriceBF9Applied = computed(() => {
  const campaignFakePriceBF9ButNoDiscount =
    discountStore.campaignDiscountCode ==
    CAMPAIGN_FAKE_PRICE_BF9_BUT_NO_DISCOUNT

  if (
    (!hasFakePriceBF9ButNoDiscountProductTag.value &&
      !campaignFakePriceBF9ButNoDiscount) ||
    !product.value
  ) {
    return
  }

  const selectedVariant = productStore.selectedVariant
  return applyFakePriceDiscountBF9(
    product.value,
    selectedVariant?.priceV2?.amount || 0,
    selectedVariant?.compareAtPriceV2?.amount || 0,
    selectedVariant?.sku
  )
})

const finalDisCountApplied = computed(() => {
  if (comboDiscountApplied.value) {
    return comboDiscountApplied.value
  } else if (donationDiscountApplied.value) {
    return donationDiscountApplied.value
  } else if (greetingCardDiscountApplied.value) {
    return greetingCardDiscountApplied.value
  } else if (packDiscountApplied.value) {
    return packDiscountApplied.value
  } else if (fakePriceBF9Applied.value) {
    return fakePriceBF9Applied.value
  }
  return discountApplied.value
})

const percentDiscountDefault = computed(() => {
  const selectedVariant = productStore.selectedVariant
  if (
    !selectedVariant ||
    !selectedVariant?.compareAtPrice ||
    !selectedVariant?.price
  ) {
    return
  }

  return Math.round(
    ((selectedVariant.compareAtPrice - selectedVariant.price) /
      selectedVariant.compareAtPrice) *
      100
  )
})

const shouldShowBuyMoreSaveMore = computed(() => {
  if (!product.value) return true

  const isHasProductTagPromotionNo = isProductHasTags(product.value, [
    PRODUCT_TAG_PROMOTION_NO,
  ])

  if (isHasProductTagPromotionNo) return false

  if (discountStore.campaignDiscountCode) {
    return !settingStore.shop?.campaignDiscountCodesThatIgnoreVolumnDiscount?.some(
      (campaignDiscountCode) =>
        discountStore.campaignDiscountCode?.toLowerCase() ===
        campaignDiscountCode.toLowerCase()
    )
  }

  return (
    !isProductHasType(
      product.value,
      settingStore.shop?.productTypesThatIgnoreVolumnDiscount || []
    ) && !isProductPackOption.value
  )
})

const isDigitalContentProductType = computed(() => {
  return isProductHasTypeStartWith(productStore?.product, 'digital')
})

onMounted(() => {
  let unWatch: WatchStopHandle | null = null
  unWatch = watch(
    () => deviceStore.preloadImageLoaded,
    (value) => {
      if (value) {
        componentStore.productPage.isShowPeopleViewing = true
        runTaskOnIdleTime(() => {
          componentStore.productPage.isShowProductDescription = true
          if (
            isExpressShipping.value ||
            isExpressShippingChina.value ||
            isProductHasTags(productStore.product, [PRODUCT_TAG_SHIP_FROM_US])
          ) {
            componentStore.productPage.isShowProductShipping = true
          }
        })
        setTimeout(() => {
          if (unWatch) {
            unWatch()
          }
        })
      }
    },
    { immediate: true }
  )

  watch(
    () => [productStore.productComboSelected, cartStore.cart?.id],
    () => {
      comboCartLineItemNotCompleted.value = getComboCartLineItemNotCompleted()
    },
    { immediate: true }
  )

  async function addProductAverageReviews() {
    // add product average reviews
    if (product.value) {
      await nextTick()
      if (!productAverageReview.value) return
      productAverageReview.value.innerHTML = productAverageReviewHtml.value
      await nextTick()
      const averageReviewElement = document.querySelector('.jdgm-widget')
      if (!averageReviewElement) return
      averageReviewElement.addEventListener('click', () => {
        // scroll to review placeholder if it exists
        if (productReviewPlaceholder.value) {
          const currentTop =
            productReviewPlaceholder.value.getBoundingClientRect().top
          window.scrollTo({
            top: window.scrollY + currentTop - HEADER_HEIGHT,
            behavior: 'smooth',
          })
        } else {
          // other wise scroll to real review section
          EventBus.trigger(EVENT_SCROLL_TO_REVIEW_SECTION)
        }
      })
    }
  }

  async function updateRecentlyViewedProducts() {
    if (product.value) {
      await saveProductToCache(product.value)
      Analytics.excuteOnReady(trackViewedNichesAndViewedProductType)
    }
  }

  watch(
    product,
    async () => {
      updateRecentlyViewedProducts()
      addProductAverageReviews()
      trackingABTestShowProductUINewVersion()
      trackingABTestNoApplyPackDiscount()
      trackingABTestProductTitle()
      
      if (product.value && hasFakePriceBF9ButNoDiscountProductTag.value) {
        const campaignBF9 = settingStore.campaigns?.find((item: any) => item?.code?.toLowerCase() == CAMPAIGN_DISCOUNT_BLACKFRIDAY9)
        componentStore.addPromotionBarContent({
          type: PROMOTION_BAR_TYPE_DISCOUNT,
          priority: PROMOTION_BAR_PRIORITY_MEDIUM,
          value: campaignBF9?.promotionMessage || 'Sale up to 70% off!',
        })
      }

      const options = {
        root: null,
        rootMargin: `-${document.body.clientHeight / 2}px 0px 0px 0px`,
        threshold: 1,
      }

      observer = new IntersectionObserver(intersectCallback, options)
      for (const key in placeHolderAndHandlerMap) {
        if (
          Object.prototype.hasOwnProperty.call(placeHolderAndHandlerMap, key)
        ) {
          const placeHolderAndHandler = placeHolderAndHandlerMap[key]
          if (
            placeHolderAndHandler.placeholder &&
            placeHolderAndHandler.placeholder.value
          ) {
            observer.observe(placeHolderAndHandler.placeholder.value)
          }
        }
      }

      function intersectCallback(entries: IntersectionObserverEntry[]) {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const componentKey = (entry.target as HTMLElement).dataset.key
            const placeholderAndHandler =
              placeHolderAndHandlerMap[componentKey || '']
            // run handler and unobserve placeholder
            if (placeholderAndHandler) {
              placeholderAndHandler.handler()
              if (placeholderAndHandler.placeholder.value) {
                if (observer) {
                  observer.unobserve(placeholderAndHandler.placeholder.value)
                }
              }
            }
          }
        })
      }

      if (
        productStore.isUILandingProduct &&
        productUniqueGiftLayoutComponentData.value?.length
      ) {
        productUniqueGiftLayoutComponentData.value.forEach((section: any) => {
          if (section._type == 'videoReviews') {
            shouldShowUniqueGiftProductComponents[section._key] = false
          } else {
            shouldShowUniqueGiftProductComponents[section._key] = true
          }
        })
        const intersectCallback = function (
          entries: IntersectionObserverEntry[]
        ) {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const componentKey = (entry.target as HTMLElement).dataset.key
              if (componentKey) {
                shouldShowUniqueGiftProductComponents[componentKey] = true
              }
              const placeholder = uniqueGiftComponentplaceholders.value.find(
                (placeholder) => placeholder.dataset.key === componentKey
              )

              if (placeholder && observer) {
                observer.unobserve(placeholder)
              }
            }
          })
        }
        const options = {
          rootMargin: `${document.body.clientHeight / 3}px`,
        }
        const observer = new IntersectionObserver(intersectCallback, options)
        setTimeout(() => {
          uniqueGiftComponentplaceholders.value.forEach((placeholder) => {
            observer.observe(placeholder)
          })
        })
      } else if (
        productUINewVersionLayoutComponentData.value?.length &&
        isProductUINewVersion.value
      ) {
        productUINewVersionLayoutComponentData.value.forEach((section: any) => {
          if (section._type == 'videoReviews') {
            shouldShowUINewVersionComponents[section._key] = false
          } else {
            shouldShowUINewVersionComponents[section._key] = true
          }
        })
        const intersectCallback = function (
          entries: IntersectionObserverEntry[]
        ) {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const componentKey = (entry.target as HTMLElement).dataset.key
              if (componentKey) {
                shouldShowUINewVersionComponents[componentKey] = true
              }
              const placeholder = uiNewVersionplaceholders.value.find(
                (placeholder) => placeholder.dataset.key === componentKey
              )
              if (placeholder && observer) {
                observer.unobserve(placeholder)
              }
            }
          })
        }
        const options = {
          rootMargin: `${document.body.clientHeight / 3}px`,
        }
        const observer = new IntersectionObserver(intersectCallback, options)
        setTimeout(() => {
          uiNewVersionplaceholders.value.forEach((placeholder) => {
            observer.observe(placeholder)
          })
        })

        window.addEventListener('scroll', debounceScroll, { passive: true })
      }
    },
    { immediate: true }
  )
  EventBus.on(EVENT_SHOW_AVERAGE_START_PRE_BAGDE, () => {
    jdgmPreviewBadgeLoading.value = true
  })
})

const productHandleClass = computed(() =>
  product.value?.handle ? `product-handle__${product.value?.handle}` : ''
)

const productTypeClass = computed(() =>
  product.value?.handle
    ? `product-type__${product.value?.productType?.replace(/\s+/g, '')}`
    : ''
)

const scrolled = [
  { percent: 10, hasScrolled: false },
  { percent: 20, hasScrolled: false },
  { percent: 30, hasScrolled: false },
  { percent: 40, hasScrolled: false },
  { percent: 50, hasScrolled: false },
  { percent: 60, hasScrolled: false },
  { percent: 70, hasScrolled: false },
  { percent: 80, hasScrolled: false },
  { percent: 90, hasScrolled: false },
  { percent: 100, hasScrolled: false },
]

const debounceScroll = debounce(onScrollPage, 250)

function onScrollPage() {
  const scrollTop = document.documentElement.scrollTop
  const documentHeight = document.documentElement.scrollHeight
  const windowHeight = window.innerHeight

  const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100

  const scrollObject = scrolled.find(
    (item) => !item.hasScrolled && scrollPercentage >= item.percent
  )
  if (scrollObject) {
    Analytics.track(
      EVENT_NEW_PRODUCT_PAGE_SCROLL,
      { percent: scrollObject.percent },
      destinationIntegrations([MIXPANEL_DESTINATION])
    )
    scrollObject.hasScrolled = true
  }
}

async function applyGoogleDiscount() {
  try {
    await CacheService.instance?.set(
      CACHE_KEY_DISCOUNT_CODE,
      googleDiscountCode.value,
      SEVEN_DAYS_IN_MILLISECONDS
    )
    await applyDiscount()

    // apply discount code for cart
    if (
      discountStore.discountCode &&
      !cartStore.discountCodeCurrentApply &&
      cartStore.cart?.lines?.length
    ) {
      await cartStore.cartDiscountCodeApply(discountStore.discountCode)
    }
  } catch (error) {
    // do nothing
  }
}

async function downloadArtwork() {
  if (!customer.value || !customerStore.accessToken) {
    EventBus.trigger(EVENT_OPEN_POPUP_SIGN_IN)
    return
  }

  loading.value = true

  const provider = await Provider.getInstance()
  const response = await provider.dowloadArtwork(
    productStore.product?.id,
    customerStore.accessToken
  )
  if (response?.success) {
    let a = document.createElement('a')
    a.href = response?.data?.image
    a.download = 'output.png'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } else {
    isError.value = true
    errorMessage.value = response?.message
  }

  loading.value = false
}

function trackingABTestProductTitle() {
  if (!product.value) return
  const abtestVariant = getABTestActivedVariantByCode(
    AB_TEST_PRODUCT_TITLE
  )
  if (product.value?.handle?.includes('alphabet-initial-for-whiskey-bourbon-lovers-personalized-whisky-whiskey-glass-10470280725scl1cle1ph08th07li09wkg1_ipatuwsk00064') ||
    product.value?.handle?.includes('whiskey-cigar-personalized-whisky-wallet-phone-case-10160040825scl1cle1ph08ha09li09lpc1_ipatuwsk00066') ||
    product.value?.handle?.includes('whiskey-and-cigar-personalized-whisky-whiskey-glass-10346220325scl1cle1ph08ha09li09wkg1_ipatuwsk00008')){
    if (abtestVariant === null) return
    const payload = {
      name: AB_TEST_PRODUCT_TITLE,
      variant: abtestVariant,
      handle: product.value?.handle,
      productTitle: productTitleBaseOnABTest.value ? productTitleBaseOnABTest.value : product.value.title,
      sku: `${abtestVariant}-${product.value?.handle.split('-')?.slice(-1)[0]}`
    }
    Analytics.track(
      EVENT_ABTEST_ASSIGNED,
      payload,
      destinationIntegrations([MIXPANEL_DESTINATION])
    )
  }
}

function trackingABTestShowProductUINewVersion() {
  if (!product.value) return
  if (
    requestLocation.countryCode &&
    requestLocation.countryCode.toUpperCase() != VN_COUNTRY_CODE &&
    !CHINA_COUNTRY_CODE.includes(requestLocation.countryCode.toUpperCase()) &&
    settingStore.productLandingPage?.showNewVersionUIProductPage &&
    productUINewVersionLayoutComponentData.value?.length
  ) {
    const abtestVariant = getABTestActivedVariantByCode(
      AB_TEST_PRODUCT_PAGE_UI_NEW_VERSION
    )
    if (abtestVariant === null) return
    const payload = {
      name: AB_TEST_PRODUCT_PAGE_UI_NEW_VERSION,
      variant: isProductUINewVersion.value
        ? AB_TEST_VARIANT_ENABLE
        : AB_TEST_VARIANT_DISABLE,
    }
    Analytics.track(
      EVENT_ABTEST_ASSIGNED,
      payload,
      destinationIntegrations([MIXPANEL_DESTINATION])
    )
  }
}

function trackingABTestNoApplyPackDiscount() {
  if (!product.value || !isProductPackOption.value) return
  const abtestVariant = getABTestActivedVariantByCode(
    AB_TEST_NO_APPLY_PACK_DISCOUNT
  )
  if (abtestVariant === null) return
  const payload = {
    name: AB_TEST_NO_APPLY_PACK_DISCOUNT,
    variant: abtestVariant,
  }
  Analytics.track(
    EVENT_ABTEST_ASSIGNED,
    payload,
    destinationIntegrations([MIXPANEL_DESTINATION])
  )
}

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
  if (
    productUINewVersionLayoutComponentData.value?.length &&
    isProductUINewVersion.value
  ) {
    window.removeEventListener('scroll', debounceScroll)
  }

  if (hasFakePriceBF9ButNoDiscountProductTag.value) {
    componentStore.removePromotionBarContent(PROMOTION_BAR_TYPE_DISCOUNT)
  }
})

const breadcrumbItems = computed(() => {
  if (!product.value) return []
  const collectionHandle = route.params.collectionHandle
  const collections = product.value.collections
  let collection
  if (collectionHandle) {
    collection = collections.find((c) => c.handle === collectionHandle)
  }
  if (!collection && productSeeMoreCollections.value) {
    collection =
      productSeeMoreCollections.value.nicheCollection ||
      productSeeMoreCollections.value.typeCollection
  }

  if (!collection) {
    collection = collections[0]
  }
  if (!collection) return []
  return [
    {
      url: buildCollectionUrl(collection.handle),
      name: collection.title,
    },
    {
      url: `/products/${product.value.handle}`,
      name: product.value.title,
    },
  ]
})
</script>

<style lang="scss">
.product-page-container {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    'b'
    'a'
    'c';

  @include media-md-up {
    grid-template-columns: 50% 1fr;
    grid-template-areas:
      'a b'
      'a c';
    column-gap: 2em;
    align-items: start;
  }
}

.product-ui-landing .product-page-container,
.product-ui-new-version .product-page-container {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    'a'
    'b'
    'c';
  @include media-md-up {
    grid-template-columns: 50% 1fr;
    grid-template-areas:
      'a b'
      'a c';
    column-gap: 2em;
    align-items: start;
  }
  .product {
    &__header {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-areas:
        'f'
        'd'
        'e';
      @include media-md-up {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-areas:
          'd'
          'e'
          'f';
      }
    }

    @include media-md-down {
      &__images {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        .carousel__button {
          background: none;
          bottom: calc(-50px - 1rem);
          top: unset;
          transform: unset;
          color: var(--text-color-secondary);
        }
        .carousel {
          margin-bottom: 0;
          &__item img {
            border-radius: 2rem;
          }
          &__dots {
            display: none;
          }
        }
      }
      &__thumbnail-slider {
        width: 80%;
        position: unset;
        display: flex;
        flex-direction: row;
        margin: auto;
      }
      &__thumbnail {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        gap: 1rem;
        min-width: calc(20% + 0.6rem);
        width: auto;
        aspect-ratio: 1;
        min-height: unset;
        border: solid 2px #ffffff;
        &::before {
          background: unset;
        }
        &--active {
          border: solid 2px var(--color-primary);
        }
        &--active,
        img {
          border-radius: 4px;
        }
      }
    }
  }
}

.collapse-customize {
  input[type='checkbox'] {
    display: none;
    &:checked {
      ~ .collapse-content {
        .collapse-header {
          .arrow svg {
            transform: rotate(90deg);
            transition: all 0.3s;
          }
        }
        .details {
          max-height: 500px;
          scrollbar-width: none;
          overflow: auto;
          transition: all 1s;
        }
      }
    }
  }

  .collapse-header {
    height: unset;
    padding: 12px 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    .arrow svg {
      transform: rotate(-90deg);
      transition: all 0.3s;
    }
    p {
      margin: 0;
    }
  }
  .details {
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s;
  }
}

.product {
  &__breadcrumb {
    margin: 0;

    @include media-md-up {
      padding: 0.5em 1.5em;
      background-color: var(--background-color);
      margin-bottom: var(--space-medium);
    }
  }

  &__images {
    grid-area: a;
  }

  &__header {
    grid-area: b;
  }

  &__info {
    grid-area: c;
  }

  &__title {
    font-size: 1.5rem;
    grid-area: d;
  }

  &__sumary-review {
    @include media-md-down {
      grid-area: f;
    }

    .jdgm-prev-badge {
      display: block !important;
    }

    .jdgm-prev-badge__stars {
      display: flex;
      gap: 6px;
      align-items: center;
      cursor: pointer;
      &::after {
        content: 'Review';
        color: var(--color-primary);
        margin-left: 1em;
        font-size: 12px;
      }
    }
    .jdgm-prev-badge__text {
      display: none;
    }
  }

  &__price {
    grid-area: e;

    font-size: 1.75rem;
    line-height: 1;
    margin: 0;
    display: grid;
    align-items: center;
    grid-template-columns:
      max-content auto
      max-content min-content;

    grid-template-areas:
      'c c c'
      'a b no-element';
    gap: 0;
    &-final {
      grid-area: a;
    }
    &-compare {
      grid-area: b;
      margin-left: 1rem;
    }
    .flash-sale-banner {
      grid-area: c;
      margin-bottom: 0.5rem;
    }
  }
  &__price-flash-sale {
    background: #fafafa;
    padding-bottom: 0.5rem;
    .product__price-final {
      margin-left: 0.5rem;
    }
    .product__price-final {
      color: var(--color-secondary);
      font-size: 2.5rem;
    }
  }

  &__price-compare {
    font-size: 1.5rem;
    text-decoration: line-through;
    color: var(--product-compare-at-price-color);
  }

  &__discount-messages {
    display: flex;
    flex-direction: column;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-primary);
  }

  &__discount-message-secondary {
    color: var(--color-secondary);
  }

  &__google-discount-messages {
    display: flex;
    gap: 0.5rem;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-secondary);
    p {
      margin-bottom: 0;
    }
  }
  &__google-discount-action {
    text-decoration: underline;
    color: var(--color-tertiary);
    cursor: pointer;
    &:hover {
      opacity: 0.8;
    }
  }

  @include media-md-up {
    &__title {
      margin-top: 0;
      font-size: 1.75rem;
    }

    &__price {
      font-size: 2.5rem;
      font-weight: bold;
      margin-bottom: 0;
    }

    &__price-compare {
      font-size: 2rem;
    }
  }

  .error-message {
    color: var(--color-secondary);
  }

  @include media-md-down {
    .divider {
      margin-bottom: 0;
    }
  }

  .d-flex-wrapper {
    display: flex;
    align-items: center;
    gap: 1rem;
    .product__button-group {
      flex: 1;
    }
  }
}
</style>
